import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import { formatCurrency, formatDate } from "../../utils/utils";
import { getDisputeDetail, getAttachmentDownloadUrl } from "../../api/disputes";
import {
  DISPUTE_STATUS,
  DISPUTE_STATUS_LABELS,
} from "../../constants/disputes";
import DisputeHistory from "./DisputeHistory";
import DisputeFormModal from "./DisputeFormModal";
import "./DisputeDetailModal.css";

// Status word color in the banner, matching the Figma (orange/red/green/grey).
const STATUS_TEXT_CLASS = {
  [DISPUTE_STATUS.UNDER_REVIEW]: "dispute-detail-status--under-review",
  [DISPUTE_STATUS.DECLINED]: "dispute-detail-status--declined",
  [DISPUTE_STATUS.SETTLED]: "dispute-detail-status--settled",
  [DISPUTE_STATUS.WITHDRAWN]: "dispute-detail-status--withdrawn",
};

const InfoRow = ({ label, value }) => (
  <div className="dispute-detail-info-row">
    <span className="dispute-detail-info-label">{label}</span>
    <span className="dispute-detail-info-value">{value || "-"}</span>
  </div>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const FinancialTile = ({ label, value, currency, highlight }) => (
  <div
    className={`dispute-detail-tile ${highlight ? "dispute-detail-tile--highlight" : ""}`}
  >
    <span className="dispute-detail-tile-label">{label}</span>
    <span className="dispute-detail-tile-value">
      {value === null || value === undefined
        ? "-"
        : formatCurrency(value, currency)}
    </span>
  </div>
);

FinancialTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
  highlight: PropTypes.bool,
};

const DisputeDetailModal = ({ isOpen, disputeId, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appealOpen, setAppealOpen] = useState(false);

  const loadDetail = useCallback(() => {
    if (!disputeId) return;
    setLoading(true);
    getDisputeDetail(disputeId)
      .then((res) => setDetail(res?.data || null))
      .catch((err) => {
        if (err?.message !== "SESSION_EXPIRED") {
          toast.error(err?.message || "Failed to load dispute details");
        }
      })
      .finally(() => setLoading(false));
  }, [disputeId]);

  useEffect(() => {
    if (isOpen && disputeId) loadDetail();
  }, [isOpen, disputeId, loadDetail]);

  const currency = detail?.currency;
  const appealEnabled =
    detail?.status === DISPUTE_STATUS.DECLINED && detail?.appealAllowed;

  const handleAppealSubmitted = () => {
    setAppealOpen(false);
    loadDetail();
    onChanged?.();
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const { url } = await getAttachmentDownloadUrl(attachment.attachmentId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (err?.message !== "SESSION_EXPIRED") {
        toast.error(err?.message || "Failed to open attachment");
      }
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        size="xl"
        centered
        contentClassName="dispute-detail-modal-content"
      >
        <ModalBody className="dispute-detail-modal-body">
          <div className="dispute-detail-header">
            <h2 className="dispute-detail-title">Dispute Details</h2>
            <button
              type="button"
              className="dispute-detail-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {loading || !detail ? (
            <div className="dispute-detail-loading">
              Loading dispute details…
            </div>
          ) : (
            <>
              <div className="dispute-detail-status-banner">
                <span className="dispute-detail-status-prefix">Status:</span>{" "}
                <span className={STATUS_TEXT_CLASS[detail.status] || ""}>
                  {DISPUTE_STATUS_LABELS[detail.status] || detail.status}
                </span>
                {detail.currentAppealRound > 0 && (
                  <span className="dispute-detail-status-meta">
                    {" "}
                    - Appeal Round: {detail.currentAppealRound}
                  </span>
                )}
              </div>

              <div className="dispute-detail-grid">
                {/* Left: information + financials */}
                <div className="dispute-detail-col">
                  <h3 className="dispute-detail-section">
                    Dispute Information
                  </h3>
                  <div className="dispute-detail-info-box">
                    <InfoRow
                      label="HT Confirmation ID:"
                      value={detail.htRoomConfirmationCode}
                    />
                    <InfoRow
                      label="Client Confirmation ID:"
                      value={detail.clientConfirmationId}
                    />
                    <InfoRow
                      label="Property Name:"
                      value={detail.propertyName}
                    />
                    <InfoRow
                      label="Disputed Date:"
                      value={formatDate(detail.disputedDate)}
                    />
                  </div>

                  <h3 className="dispute-detail-section">Dispute Reason</h3>
                  <div className="dispute-detail-description">
                    {detail.disputeReason || "-"}
                  </div>

                  <h3 className="dispute-detail-section">Issue Description</h3>
                  <div className="dispute-detail-description">
                    {detail.issueDescription || "-"}
                  </div>

                  <h3 className="dispute-detail-section">Financial Details</h3>
                  <div className="dispute-detail-tiles">
                    <FinancialTile
                      label="Transacted Booking Amount at Dispute"
                      value={detail.transactedBookingAmount}
                      currency={currency}
                    />
                    <FinancialTile
                      label="Client Agreed Booking Amount"
                      value={detail.clientAgreedBookingAmount}
                      currency={currency}
                    />
                    <FinancialTile
                      label="Disputed Booking Amount"
                      value={detail.disputedBookingAmount}
                      currency={currency}
                      highlight
                    />
                    <FinancialTile
                      label="Current Transacted Booking Amount"
                      value={detail.latestTransactedBookingAmount}
                      currency={currency}
                    />
                    <FinancialTile
                      label="Platform Fee at Dispute"
                      value={detail.platformFeeAtDispute}
                      currency={currency}
                    />
                    <FinancialTile
                      label="Current Platform Fee"
                      value={detail.latestPlatformFee}
                      currency={currency}
                    />
                  </div>
                </div>

                {/* Right: history */}
                <div className="dispute-detail-col">
                  <h3 className="dispute-detail-section">
                    Dispute History &amp; Appeal Rounds
                  </h3>
                  <div className="dispute-detail-history-box">
                    <DisputeHistory
                      history={detail.history}
                      currency={currency}
                      onDownloadAttachment={handleDownloadAttachment}
                    />
                  </div>
                </div>
              </div>

              <div className="dispute-detail-footer">
                <button
                  type="button"
                  className="dispute-btn dispute-btn--secondary"
                  onClick={() => setAppealOpen(true)}
                  disabled={!appealEnabled}
                  title={
                    appealEnabled
                      ? undefined
                      : "This decision cannot be appealed"
                  }
                >
                  Appeal this Decision
                </button>
                <button
                  type="button"
                  className="dispute-btn dispute-btn--primary"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      {detail && (
        <DisputeFormModal
          isOpen={appealOpen}
          mode="appeal"
          disputeId={detail.id}
          htConfirmationId={detail.htConfirmationId}
          currentTransactedBookingAmount={detail.latestTransactedBookingAmount}
          defaultAgreedAmount={detail.clientAgreedBookingAmount}
          bookingDetails={{
            propertyName: detail.propertyName,
            clientConfirmationId: detail.clientConfirmationId,
          }}
          onClose={() => setAppealOpen(false)}
          onSubmitted={handleAppealSubmitted}
        />
      )}
    </>
  );
};

DisputeDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  disputeId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};

export default DisputeDetailModal;
